<template>
  <transition :name="transition">
    <div v-show="isActive" tabindex="0" class="overlay is-active" :class="{ 'is-full-page': isFullPage }" :aria-busy="isActive" aria-label="Loading" :style="{ zIndex: zIndex }">
      <div class="background" :style="{ background: backgroundColor, opacity: opacity }" @click.prevent="cancel" />
      <div class="icon">
        <slot name="before" />
        <slot name="default">
          <d-loader :type="loader" />
        </slot>
        <slot name="after" />
      </div>
    </div>
  </transition>
</template>

<script>
  import { removeElement } from '../../../utils/helpers';
  import { HTMLElement } from '../../../utils/ssr';
  import DLoader from '../../elements/DLoader/DLoader.vue';

  /**
   * @displayName LoadingOverlay
   */

  export default {
    name: 'DLoadingOverlay',
    status: 'prototype',
    release: '0.1.0',
    components: { DLoader },
    props: {
      active: Boolean,

      /**
       * Utilized primarily when used as a plugin. Dynamically attach to a container using the `$refs`
       */
      programmatic: {
        type: Boolean,
        default: false,
      },

      /**
       * Automatically markas as active and attaches to the full page (or container). Only shown for 150ms
       */
      container: {
        type: [Object, Function, HTMLElement],
        default: null,
      },
      isFullPage: {
        type: Boolean,
        default: true,
      },
      transition: {
        type: String,
        default: 'fade',
      },

      /**
       * Allow user to hide the loader
       */
      canCancel: Boolean,

      /**
       * Do something on cancel
       */
      onCancel: {
        type: Function,
        default: () => { },
      },
      color: String,
      backgroundColor: String,

      /**
       * Higher the number; harder it is to see the underlying content. On a scale from 0 - 1
       */
      opacity: {
        type: Number,
        default: 0.8,
        validator: value => value >= 0 && value <= 1,
      },
      width: Number,
      height: Number,
      zIndex: Number,

      /**
       * `dots, spinner`
       */
      loader: {
        type: String,
        default: 'spinner',
      },
    },
    data() {
      return {
        // Don't mutate the prop
        isActive: this.active || false,
      };
    },
    watch: {
      active(value) {
        this.isActive = value;
      },
    },
    beforeMount() {
      // Insert the component in the body tag when called programmatically
      if (this.programmatic) {
        if (this.container) {
          this.isFullPage = false;
          this.container.appendChild(this.$el);
        } else {
          document.body.appendChild(this.$el);
        }
      }
    },

    created() {
      if (typeof window !== 'undefined') {
        document.addEventListener('keyup', this.keyPress);
      }
    },

    mounted() {
      if (this.programmatic) this.isActive = true;
    },

    beforeDestroy() {
      if (typeof window !== 'undefined') {
        document.removeEventListener('keyup', this.keyPress);
      }
    },

    methods: {
      /**
       * Proxy to hide() method.
       * Gets called by ESC button or when click outside
       */
      cancel() {
        if (!this.canCancel || !this.isActive) return;
        this.close();
      },

      /**
       * Hide and destroy component if it's programmatic.
       */
      close() {
        this.onCancel.apply(null, arguments);
        this.$emit('close');
        this.$emit('update:active', false);

        // Timeout for the animation complete before destroying
        if (this.programmatic) {
          this.isActive = false;
          setTimeout(() => {
            this.$destroy();
            removeElement(this.$el);
          }, 150);
        }
      },

      /**
       * Key press event to hide on ESC.
       *
       * @param event
       */
      keyPress(event) {
        if (event.keyCode === 27) this.cancel();
      },
    },
  };
</script>